import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { isEqual, partition, sortBy, uniqBy, uniqWith } from 'lodash-es';
import moment from 'moment';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useLayoutEffect, useState } from 'react';
import ActivityItem, { ActivitySeparator } from './ActivityItem';
import * as styles from './ControlBarActivity.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import SVGIcon from '../../components/SVGIcon';
import { EditorActions } from '../../editor/editorSlice';
import { useQuill } from '../../editor/QuillContext';
import isInlineTextInsertion from '../../editor/utils/isInlineTextInsertion';
import { gql } from '../../graphql/generated';
import type { DocumentType } from '../../graphql/generated/gql';
import { AlphaPostActivityType } from '../../graphql/generated/graphql';
import archiveIcon from '../../icons/non-prefilled/16x16/archive.svg';
import commentIcon from '../../icons/non-prefilled/16x16/comment.svg';
import draftsIcon from '../../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../../icons/non-prefilled/16x16/edit.svg';
import publishIcon from '../../icons/non-prefilled/16x16/publish.svg';
import reorderIcon from '../../icons/non-prefilled/16x16/reorder.svg';
import replyIcon from '../../icons/non-prefilled/16x16/reply.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../../icons/non-prefilled/16x16/unpublish.svg';
import verifiedIcon from '../../icons/non-prefilled/16x16/verifiedFilled.svg';
import { useGetUser } from '../../user/useAllUsers';
import exhaustiveMatchGuard from '../../utils/exhaustiveMatchGuard';
import { isFeatureEnabled } from '../../utils/features';
import Logger from '../../utils/Logger';
import { isCommentOrPublicCommentThread } from '../../utils/post/threadUtils';
import getFirstName from '../../utils/text/getFirstName';
import historyPath from '../../utils/url/historyPath';
import CommentPreview from '../comment/CommentPreview';
import partitionAndGroupHistories from '../history/partitionAndGroupHistories';
import type { HistoryGroup } from '../history/partitionAndGroupHistories';
import sumHistoryStats from '../history/sumHistoryStats';
import Stats from '../Stats';
import useCanDisplayNewPostHistoryView from '../useCanDisplayNewPostHistoryView';
import collectThreadAndAnnotationIds from '../utils/collectThreadAndAnnotationIds';

export const CONTROL_BAR_ACTIVITY_MIN_HEIGHT = 265;

// Do initial fetch with a fixed time to enable caching
// TODO: This will not be needed when beforeAt becomes optional
export const POST_ACTIVITIES_END_OF_TIME = '2099-12-31T00:00:00.0Z';

const MAX_COMMENT_LINE_COUNT = 3;

const CONTROL_BAR_ACTIVITY_QUERY = gql(/** @complexity 27 */ `
  query ControlBarActivity($postId: ID!, $beforeAt: Datetime!) {
    alphaPostActivities(postId: $postId, beforeAt: $beforeAt) {
      actedAt
      action
      actors {
        id
        ...useGetUser_user
      }
      source {
        id
        ... on Comment {
          content
        }
        ... on CommentThread {
          content
        }
      }
    }
    post(id: $postId) {
      id
      threads {
        id
        ... on CommentThread {
          id
          comments {
            id
            insertedAt
            content
            author {
              id
              ...useGetUser_user
            }
          }
        }
      }
      histories {
        id
        ...partitionAndGroupHistories_history
        ...sumHistoryStats_history
        authors {
          id
          ...useGetUser_user
        }
      }
      ...historyPath_post
    }
  }
`);

interface ControlBarActivityProps {
  postId: string;
  closePopover: () => void;
  fullHeight?: boolean;
  lastViewedAt: number;
}

const ControlBarActivity = ({
  postId,
  closePopover,
  lastViewedAt,
  fullHeight,
}: ControlBarActivityProps) => {
  const { data, loading, fetchMore } = useQuery(CONTROL_BAR_ACTIVITY_QUERY, {
    variables: {
      postId,
      beforeAt: POST_ACTIVITIES_END_OF_TIME,
    },
  });

  // TODO: Add error handling
  if (loading || !data) {
    return (
      <div className={styles.loader}>
        <LoadingIndicator delay={0} />
      </div>
    );
  }

  const fetchMoreActivities = async () => {
    const lastActivity = data.alphaPostActivities?.at(-1);
    if (!lastActivity) {
      return;
    }

    // There might be two activities with the same timestamp on the edge of two pages.
    // To ensure we don't skip any of them, we slightly over-fetch to include
    // the last activity from the previous page on the next page.
    const beforeAt = moment(lastActivity.actedAt).add(1, 'ms').toISOString();

    await fetchMore({
      variables: {
        postId,
        beforeAt,
      },
      updateQuery(previousResult, { fetchMoreResult }) {
        return {
          ...previousResult,
          ...fetchMoreResult,
          alphaPostActivities: uniqWith(
            [
              ...previousResult.alphaPostActivities,
              ...fetchMoreResult.alphaPostActivities,
            ],
            isEqual,
          ),
        };
      },
    });
  };

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
      })}
    >
      <UnresolvedActivitiesSection
        threads={data.post.threads}
        onSelectThread={closePopover}
      />
      <AllActivitiesSection
        post={data.post}
        activities={data.alphaPostActivities}
        fetchMoreActivities={fetchMoreActivities}
        lastViewedAt={lastViewedAt}
      />
    </div>
  );
};

interface UnresolvedActivitiesSectionProps {
  threads?: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post']['threads'];
  onSelectThread: () => void;
}

const UnresolvedActivitiesSection = ({
  threads,
  onSelectThread,
}: UnresolvedActivitiesSectionProps) => {
  const { quill } = useQuill();
  const dispatch = useAppDispatch();
  const [postThreadsIds, setPostThreadsIds] = useState(new Set<string>());
  const getUser = useGetUser();

  useLayoutEffect(() => {
    if (!quill) return undefined;

    const updatePostThreadsIds = () => {
      const result = collectThreadAndAnnotationIds(quill.getContents().ops);
      setPostThreadsIds((current) =>
        isEqual(current, result.allThreadIds) ? current : result.allThreadIds,
      );
    };

    updatePostThreadsIds();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      updatePostThreadsIds();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill]);

  const visibleCommentThreads = threads
    ?.map((thread) => {
      const lastComment =
        isCommentOrPublicCommentThread(thread) &&
        postThreadsIds.has(thread.id) &&
        thread.comments.at(-1);

      if (!lastComment) {
        return null;
      }

      return {
        id: thread.id,
        lastComment,
        hasReplies: thread.comments.length > 1,
        timestamp: new Date(lastComment.insertedAt).getTime(),
      };
    })
    .filter((item) => item != null)
    .toSorted((a, b) => b.timestamp - a.timestamp);

  if (!visibleCommentThreads?.length) {
    return null;
  }

  const focusCommentThread = (threadId: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id: threadId,
        type: 'comment',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
    onSelectThread();
  };

  return (
    <>
      <h3 className={styles.title}>Unresolved</h3>
      <div>
        {visibleCommentThreads.map((thread) => {
          return (
            <ActivityItem
              key={thread.id}
              icon={
                <SVGIcon className={styles.commentIcon}>
                  {thread.hasReplies ? replyIcon : commentIcon}
                </SVGIcon>
              }
              description={
                <CommentPreview
                  collapseLines
                  content={thread.lastComment.content}
                  link={false}
                  maxLines={MAX_COMMENT_LINE_COUNT}
                />
              }
              timestamp={thread.timestamp}
              actors={[getUser(thread.lastComment.author)]}
              withActorsNames
              onClick={() => focusCommentThread(thread.id)}
              onMouseEnter={() =>
                dispatch(EditorActions.highlightComment(thread.id))
              }
              onMouseLeave={() =>
                dispatch(EditorActions.removeMagneticWallHighlight())
              }
            />
          );
        })}
      </div>
    </>
  );
};

interface AllActivitiesSectionProps {
  post: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post'];
  activities: DocumentType<
    typeof CONTROL_BAR_ACTIVITY_QUERY
  >['alphaPostActivities'];
  fetchMoreActivities: () => void;
  lastViewedAt: number;
}

const AllActivitiesSection = ({
  activities,
  post,
  lastViewedAt,
  fetchMoreActivities,
}: AllActivitiesSectionProps) => {
  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();
  const getUser = useGetUser();

  const recentActivitiesThreshold = lastViewedAt ? moment(lastViewedAt) : null;

  const lastFetchedActivity = activities.at(-1);

  type ActivityItemType = {
    timestamp: number;
    key: string;
    view: React.ReactNode;
  };

  const postActivities = activities
    .filter((item) => item.action !== AlphaPostActivityType.Edit)
    .map(
      (item): ActivityItemType => ({
        key: `${item.actedAt}-${item.action}`,
        timestamp: new Date(item.actedAt).getTime(),
        view: <PostActivity activity={item} />,
      }),
    );

  const { accessibleHistories, restrictedHistories } =
    partitionAndGroupHistories(post.histories);

  const historyActivities = [...accessibleHistories, ...restrictedHistories]
    .filter(
      (item) =>
        new Date(lastFetchedActivity?.actedAt) <= new Date(item.insertedAt),
    )
    .map((item): ActivityItemType => {
      const stats = sumHistoryStats(item.histories);
      const hasStats = Object.values(stats).some((stat) => stat > 0);
      const actors = uniqBy(
        item.histories.flatMap((history) => history.authors.map(getUser)),
        (user) => user.id,
      );
      return {
        key: `${item.insertedAt}-history}`,
        timestamp: new Date(item.insertedAt).getTime(),
        view: (
          <ActivityItem
            actors={actors}
            icon={<SVGIcon className={styles.editIcon}>{editIcon}</SVGIcon>}
            timestamp={item.insertedAt}
            description={
              hasStats ? (
                <Stats stats={sumHistoryStats(item.histories)} />
              ) : (
                'Edited this post'
              )
            }
            href={getLinkToHistory(post, item, !!canDisplayNewPostHistoryView)}
          />
        ),
      };
    });

  const allActivities = uniqBy(
    [...postActivities, ...historyActivities],
    (item) => item.key,
  );
  const allActivitiesSorted = sortBy(allActivities, (item) => -item.timestamp);

  const [recentActivities, oldActivities] = partition(
    allActivitiesSorted,
    (item) => recentActivitiesThreshold?.isBefore(item.timestamp),
  );

  const hasMoreActivitiesToFetch =
    lastFetchedActivity &&
    lastFetchedActivity.action !== AlphaPostActivityType.Create;

  return (
    <>
      <h3 className={styles.title}>Activities</h3>
      <div>
        {recentActivities?.map((activity) => (
          <React.Fragment key={activity.key}>{activity.view}</React.Fragment>
        ))}
        {oldActivities.length > 0 && (
          <>
            {recentActivitiesThreshold && (
              <ActivitySeparator>
                Your last view {recentActivitiesThreshold.fromNow()}
              </ActivitySeparator>
            )}
            {oldActivities?.map((activity) => (
              <React.Fragment key={activity.key}>
                {activity.view}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
      {hasMoreActivitiesToFetch && (
        <InfiniteScrollWaypoint fetchMore={fetchMoreActivities} />
      )}
    </>
  );
};

const getLinkToHistory = (
  post: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post'],
  historyGroup: HistoryGroup<
    DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post']['histories'][number]
  >,
  canDisplayNewPostHistoryView: boolean,
) => {
  const path = historyPath(
    post,
    historyGroup.versionFrom,
    historyGroup.versionTo,
  );

  if (!isFeatureEnabled('history_v2')) {
    return path;
  }

  return historyGroup.isAccessible && canDisplayNewPostHistoryView
    ? path
    : undefined;
};

interface PostActivityProps {
  activity: DocumentType<
    typeof CONTROL_BAR_ACTIVITY_QUERY
  >['alphaPostActivities'][number];
}

const PostActivity = ({ activity }: PostActivityProps) => {
  const getUser = useGetUser();
  const actors = activity.actors.map(getUser);

  const descriptionWithActors = (action: React.ReactNode) => (
    <div className={styles.activityDescription}>
      <strong>
        {activity.actors
          .map((actor) => getFirstName(getUser(actor).name))
          .join(', ')}
      </strong>{' '}
      {action}
    </div>
  );

  switch (activity.action) {
    case AlphaPostActivityType.Edit:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.editIcon}>{editIcon}</SVGIcon>}
          description={descriptionWithActors('edited this post')}
        />
      );
    case AlphaPostActivityType.Create:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.createIcon}>{draftsIcon}</SVGIcon>}
          description={descriptionWithActors('created this post')}
        />
      );
    case AlphaPostActivityType.Verify:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.verifyIcon}>{verifiedIcon}</SVGIcon>}
          description={descriptionWithActors('verified this post')}
        />
      );
    case AlphaPostActivityType.Publish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.publishIcon}>{publishIcon}</SVGIcon>}
          description={descriptionWithActors('published this post')}
        />
      );
    case AlphaPostActivityType.Unpublish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unpublishIcon}>{unpublishIcon}</SVGIcon>
          }
          description={descriptionWithActors('unpublished this post')}
        />
      );
    case AlphaPostActivityType.Archive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.archiveIcon}>{archiveIcon}</SVGIcon>}
          description={descriptionWithActors('archived this post')}
        />
      );
    case AlphaPostActivityType.Unarchive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unarchiveIcon}>{unarchiveIcon}</SVGIcon>
          }
          description={descriptionWithActors('unarchived this post')}
        />
      );
    case AlphaPostActivityType.TransferOwner:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.transferIcon}>{reorderIcon}</SVGIcon>
          }
          description={descriptionWithActors(
            <>
              transferred ownership
              {activity.source?.__typename === 'User' && (
                <>
                  {' '}
                  to{' '}
                  <strong>{getFirstName(getUser(activity.source).name)}</strong>
                </>
              )}
            </>,
          )}
        />
      );
    case AlphaPostActivityType.CommentCreate:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.commentIcon}>{commentIcon}</SVGIcon>}
          description={
            <CommentPreview
              collapseLines
              content={
                activity.source?.__typename === 'Comment' &&
                activity.source?.content
              }
              link={false}
              maxLines={MAX_COMMENT_LINE_COUNT}
            />
          }
        />
      );
    case AlphaPostActivityType.CommentReply:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.commentIcon}>{replyIcon}</SVGIcon>}
          description={
            <CommentPreview
              collapseLines
              content={
                activity.source?.__typename === 'Comment' &&
                activity.source.content
              }
              link={false}
              maxLines={MAX_COMMENT_LINE_COUNT}
            />
          }
        />
      );
    case AlphaPostActivityType.CommentResolve:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.resolveIcon}>{commentIcon}</SVGIcon>}
          description={
            <CommentPreview
              collapseLines
              content={
                activity.source?.__typename === 'CommentThread' &&
                activity.source.content
              }
              link={false}
              maxLines={MAX_COMMENT_LINE_COUNT}
            />
          }
        />
      );
    default: {
      exhaustiveMatchGuard(activity.action);
      const error = `Unknown activity type: ${activity.action as string}`;
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(error);
      }
      Logger.error(error);
      return null;
    }
  }
};

export default ControlBarActivity;
